import LazyLoadedImage from 'Components/shared/LazyLoadedImage';
import { Component, useContext } from 'react';
import { Link } from 'react-router-dom';
import {
	TAGS as tags,
	getAlbums,
	matchesTags,
	sessionAlbumIds,
	photoSrc,
	themeVariant,
} from 'modules/gallery';
import Theme from 'contexts/Theme';
import shuffle from 'modules/utils/shuffle';

class PhotoGallery extends Component {
	constructor() {
		super();

		this.state = {
			metadata: {},
			activeTags: {},
		};
	}

	fetchMetadata = async () => {
		const metadata = await getAlbums();
		const collections = sessionAlbumIds(metadata, shuffle);

		this.setState({ metadata, collections, loaded: true });
	};

	componentDidMount() {
		this.fetchMetadata();
	}

	toggleTag = (tag) => {
		const activeTags = {
			...this.state.activeTags,
			[tag]: !this.state.activeTags[tag],
		};

		this.setState({
			activeTags,
		});
	};

	render() {
		const { metadata, loaded, activeTags, collections } = this.state;
		const { theme } = this.props;

		if (!loaded) {
			return;
		}

		let imagesMap = [];

		for (const [index, collection] of Object.entries(collections)) {
			const collectionMetadata = metadata[collection];
			const fileName = collectionMetadata.coverImage;
			const path = `${collection}/${fileName}`;
			const photo = collectionMetadata.photos[fileName];

			if (!matchesTags(collectionMetadata, activeTags)) continue;

			imagesMap.push(
				<Link
					key={path}
					to={`/photo-collection/${collection}`}
					className={`${
						photo?.orientation ===
						'landscape'
							? 'col-[span_3_/_auto]'
							: ''
					}  `}>
					<div className='group overflow-clip relative'>
						<div className='absolute peer w-full h-1/2 bottom-0 left-0 z-[999999]' />
						<LazyLoadedImage
							notLazy={index === 0}
							width={photo?.width}
							height={photo?.height}
							src={photoSrc(
								collection,
								themeVariant(theme),
								fileName,
								photo
							)}
							className={`shadow-lg w-full h-full transition-all duration-300 sm:peer-hover:blur-[2px] blur-0 ${
								photo?.fit === false
									? ''
									: 'object-cover'
							}`}
							alt={path}
							placeholderSrc={photoSrc(
								collection,
								'lowres',
								fileName,
								photo
							)}
						/>
						<div
							className={`absolute flex flex-row justify-center place-content-center my-10 w-full bottom-0 opacity-0 sm:peer-hover:opacity-100 translate-all duration-300 z-10`}>
							<span className='text font-standard text-textContent'>
								{collectionMetadata.name}
							</span>
						</div>
						<div className='w-full h-full bg-gradient-to-b from-transparent to-[rgba(0,0,0,0.95)] absolute top-0 left-0 opacity-0 sm:peer-hover:opacity-100 transition-all duration-300 z-0' />
						<div className='bg-[url("https://framerusercontent.com/images/rR6HYXBrMmX4cRpXfXUOvpvpB0.png")] sm:peer-hover:opacity-[0.02] opacity-0 inset-[-200%] w-[400%] h-[400%] absolute transform-[translateX(-10%) translateY(10%) translateZ(0px)]' />
					</div>
				</Link>
			);

			// Everyhing after the first image is shuffled
		}

		const tagsMap = tags.map((tag) => {
			const active = activeTags[tag];

			return (
				<span
					key={tag}
					className={`text-textContent text-xs sm:text-sm rounded-full font-standard transition-all duration3-00 text-nowrap ${
						active ? 'bg-slate-500' : 'bg-[rgba(50,50,50,0.7)]'
					} px-5 py-2 cursor-pointer select-none`}
					onClick={() => {
						this.toggleTag(tag);
					}}>
					{tag}
				</span>
			);
		});

		return (
			<div className='m-5 mb-16 group sm:relative'>
				{/* Desktop: overlays the top of the gallery and scrolls away
				    with it (shown on hover). Mobile: fixed at the bottom. */}
				<div className='fixed sm:absolute z-[9999] sm:h-10 max-w-full flex-wrap sm:max-w-full w-full px-5 sm:px-0 left-0 bottom-0 sm:top-0 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-row gap-2 place-items-center justify-center pb-5 '>
					{tagsMap}
				</div>
				<div className='grid grid-cols-3 gap-2 sm:gap-[25px] auto-rows-auto grid-flow-dense group-hover:translate-y-[50px] duration-300 transition-all'>
					{imagesMap}
				</div>
			</div>
		);
	}
}

export default (props) => {
	const theme = useContext(Theme);

	return <PhotoGallery {...props} theme={theme} />;
};
